import styled from 'styled-components'
import React, { useMemo } from 'react'
import PPVTemplate from 'src/components/page/ppv'
import { commonCtaText } from 'src/modules/utils/constants'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'
import { WEB_MARKETING_INTERNATIONAL_FRIENDLY_SCHEDULER } from 'src/modules/reducers/cohorts/constants'
import HeroCard from 'src/components/common/HeroCard'
import { body, typographyMixin } from 'src/constants/marketing-ds'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { useSelector } from 'react-redux'
import { selectStateForProtobuf } from 'src/modules/reducers'

const PPV_FLAGS = {
  nov_7: 'nov_7',
  nov_13: 'nov_13',
  nov_15: 'nov_15',
  nov_18: 'nov_18',
}

const ppvPromoCodes = {
  col_v_nz: 'us-rc-ga-col_v_nz-251115-ppv-6',
  col_v_aus: 'us-rc-ga-col_v_aus-251118-ppv-7',
  can_v_ecu: 'us-rc-ga-can_v_ecu-251113-ppv-8',
  ecu_v_nz: 'us-rc-ga-ecu_v_nz-251118-ppv-9',
  ecu_pass: 'us-rc-ga-ecu_pass-251113-18-ppv-1',
  col_pass: 'us-rc-ga-col_pass-251113-18-ppv-1',
}

const prices = {
  all_matches: 35.95,
  single_match: 24.95,
}

export default (props) => {
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const flagValue = useCohortFlag(WEB_MARKETING_INTERNATIONAL_FRIENDLY_SCHEDULER)
  const isFlagNone = flagValue === 'none'
  const isFlagNov18OrNone = flagValue === 'nov_18' || flagValue === 'none'

  const handleOnClick = () => {
    fireUIInteractionEvent(
      {
        element: 'Buy one match for $' + prices.single_match,
        component: 'hero_card_cta',
        type: 'click',
      },
      stateForProtobuf
    )
  }

  const ppvEvents = useMemo(() => {
    return [
      {
        thumbnail: 'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/ecuador-mvp-pass-2025.png',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13],
        id: 'ecu_mvp_pass',
        title: 'Ecuador MVP Pass',
        ratePlanCode: ppvPromoCodes.ecu_pass,
        hideMeta: true,
        price: prices.all_matches,
        isAllMatches: true,
        ctaText: 'Ecuador MVP Pass $35.95',
      },
      {
        thumbnail: 'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/colombia-mvp-pass-2025.png',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13, PPV_FLAGS.nov_15],
        id: 'col_mvp_pass',
        title: 'Colombia MVP Pass',
        ratePlanCode: ppvPromoCodes.col_pass,
        hideMeta: true,
        price: prices.all_matches,
        isAllMatches: true,
        ctaText: 'Colombia MVP Pass $35.95',
      },
      {
        text: 'Canada v Ecuador (11/13)',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13],
        available: true,
        purchasable: true,
        id: 'can_v_ecu',
        title: 'Canada v Ecuador',
        thumbnail: 'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/canada-v-ecuador-2025.png',
        channel: 'Pay Per View 8',
        date: '11/13',
        time: '7:45 PM ET',
        ratePlanCode: ppvPromoCodes.can_v_ecu,
        price: prices.single_match,
      },
      {
        text: 'Colombia v New Zealand (11/15)',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13, PPV_FLAGS.nov_15],
        available: true,
        purchasable: true,
        id: 'col_v_nz',
        title: 'Colombia v New Zealand',
        thumbnail:
          'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/colombia-v-new-zealand-2025.png',
        channel: 'Pay Per View 6',
        date: '11/15',
        time: '7:30 PM ET',
        ratePlanCode: ppvPromoCodes.col_v_nz,
        price: prices.single_match,
      },

      {
        text: flagValue === PPV_FLAGS.nov_15 ? null : 'Ecuador v New Zealand (11/18)',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13, PPV_FLAGS.nov_15],
        available: flagValue === PPV_FLAGS.nov_15 ? undefined : true,
        purchasable: true,
        id: 'ecu_v_nz',
        title: 'Ecuador v New Zealand',
        thumbnail:
          'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/ecuador-v-new-zealand-2025.png',
        channel: 'Pay Per View 9',
        date: '11/18',
        time: '8:30 PM ET',
        ratePlanCode: ppvPromoCodes.ecu_v_nz,
        price: prices.single_match,
      },
      {
        text: 'Colombia v Australia (11/18)',
        flags: [PPV_FLAGS.nov_7, PPV_FLAGS.nov_13, PPV_FLAGS.nov_15],
        available: true,
        purchasable: true,
        id: 'col_v_aus',
        title: 'Colombia v Australia',
        thumbnail:
          'https://assets-imgx.fubo.tv/marketing/lp/images/ppv/colombia-v-australia-2025.png',
        channel: 'Pay Per View 7',
        date: '11/18',
        time: '8:30 PM ET',
        ratePlanCode: ppvPromoCodes.col_v_aus,
        price: prices.single_match,
      },
    ].filter((event) => event.flags.includes(flagValue))
  }, [flagValue])

  const cardOrder = [
    'can_v_ecu', // Canada v Ecuador (Ecuador match)
    'ecu_v_nz', // Ecuador v New Zealand (Ecuador match)
    'ecu_mvp_pass', // Ecuador MVP Pass
    'col_v_nz', // Colombia v New Zealand (Colombia match)
    'col_v_aus', // Colombia v Australia (Colombia match)
    'col_mvp_pass', // Colombia MVP Pass
  ]

  const reorderedPPVEvents = useMemo(() => {
    return [...ppvEvents].sort((a, b) => {
      const indexA = cardOrder.indexOf(a.id)
      const indexB = cardOrder.indexOf(b.id)

      // If an item is not in cardOrder, put it at the end
      if (indexA === -1) return 1
      if (indexB === -1) return -1

      return indexA - indexB
    })
  }, [ppvEvents])

  return (
    <StyledContainer>
      <PPVTemplate
        lang="en"
        market="us"
        location={props.location}
        video={'https://assets.fubo.tv/marketing/lp/videos/compressed/LP_PPV_CONCACAF_WCQ.mp4'}
        title="Stream international friendly matches"
        subtitle={
          isFlagNov18OrNone ? null : (
            <>
              Save 25% with a 2-game MVP Pass.{' '}
              <span className="desktop-line-break">Only with Fubo Pay Per View.</span>
            </>
          )
        }
        additionalFAQs={additionalFAQs}
        platformImage="https://assets-imgx.fubo.tv/marketing/lp/images/devices/ca-devices-2025.png"
        pageDescription="Watch England, Canada, Colombia and more international friendlies with Pay Per View"
        ctaText={commonCtaText.getStarted}
        isEventEnded={isFlagNone}
        heroFootnote="Fubo subscription not required"
        noHeroCTA={isFlagNov18OrNone ? false : true}
        ppvEvents={ppvEvents}
        isMultiPPV={ppvEvents.length > 0}
        multiPPVTitle={
          <>
            Get a 2-game MVP Pass <span className="desktop-line-break">for only $35.95.</span>
          </>
        }
        multiPPVBody="Or buy single matches for $24.95 each."
        heroCTAs={
          flagValue === PPV_FLAGS.nov_18
            ? [
                {
                  text: 'Ecuador v New Zealand (11/18) for $24.95',
                  offerCode: ppvPromoCodes.ecu_v_nz,
                },
                {
                  text: 'Colombia v Australia (11/18) for $24.95',
                  offerCode: ppvPromoCodes.col_v_aus,
                },
              ]
            : null
        }
        heroSideComponent={
          reorderedPPVEvents.length > 0 && flagValue !== PPV_FLAGS.nov_18 ? (
            <HeroCard
              title="Get your 2-game MVP Pass"
              backgroundImage="https://assets-imgx.fubo.tv/marketing/lp/images/ppv/international-friendly-card.png?ch=width&auto=format,compress"
              footnote={
                <FootnoteLink onClick={handleOnClick} href="#matches">
                  Single matches ${prices.single_match}
                </FootnoteLink>
              }
              isFootnoteInCard={true}
              listItems={reorderedPPVEvents}
              offerCode={ppvPromoCodes.col_pass}
            />
          ) : null
        }
      />
    </StyledContainer>
  )
}

const FootnoteLink = styled.a`
  ${typographyMixin(body.md)}
  text-decoration: underline;
`

const StyledContainer = styled.div`
  .content-container.has-side-component .hero-content,
  .content-container .hero-content {
    max-width: 670px !important;
  }
`

const additionalFAQs = [
  {
    question: 'Is the Canada v Ecuador match available to watch on Fubo?',
    answer:
      'Yes, the Canada v Ecuador match is available to watch with Pay Per View on Fubo on November 13.',
  },
  {
    question: 'How can I find the Ecuador v New Zealand match on Fubo?',
    answer:
      'You can find the Ecuador v New Zealand match by searching for it in the Fubo guide or finding the Pay Per View channels in the channel guide. The match is on November 18.',
  },
  {
    question: 'Can I watch the Colombia v New Zealand match live on Fubo?',
    answer:
      'Yes, the November 15 Colombia v New Zealand match will be available for live viewing on Fubo Pay Per View.',
  },
  {
    question: 'Where can I find the Colombia v Australia match on Fubo?',
    answer:
      'The Colombia v Australia match on November 18 can be found in the live sports section of Fubo. You can also use the search feature to locate it quickly.',
  },
]
